import { supabase } from './supabase';
import { shortAddress, type FoundPlace } from '../../supabase/functions/_shared/placeSearch.ts';
import { NOTES_MAX, NOTES_MIN } from '../../supabase/functions/_shared/placeSuggest.ts';

export type { FoundPlace };
export { shortAddress, NOTES_MAX, NOTES_MIN };

/** The fields the picker fills in. Both the Event Library and the itinerary editor map onto this. */
export interface PlaceFieldsValue {
  name: string;
  category: string;
  address: string | null;
  latitude: number | null;
  longitude: number | null;
  description: string;
  tip: string | null;
}

/** Which fields hold a draft the host has not touched yet (shown as a small "suggested" tag). */
export type SuggestedField = 'name' | 'category' | 'description';

export interface CategoryOption { slug: string; name: string }

export async function searchPlaces(q: string, near: { lat: number; lng: number } | null): Promise<FoundPlace[]> {
  const { data, error } = await supabase.functions.invoke('place-search', { body: { q, near } });
  if (error) throw error;
  return ((data as { places?: FoundPlace[] } | null)?.places ?? []);
}

/** Some of the host's own descriptions, so the new one sounds like them. Best effort: none is fine. */
export async function loadStyleExamples(hostId: string): Promise<string[]> {
  try {
    const { data } = await supabase
      .from('event_library')
      .select('description')
      .eq('host_id', hostId)
      .order('updated_at', { ascending: false })
      .limit(8);
    return ((data ?? []) as { description: string | null }[])
      .map((r) => (r.description ?? '').trim())
      .filter((d) => d.length >= 40)
      .slice(0, 3);
  } catch {
    return [];
  }
}

export interface DescribeRequest {
  place: { name: string; kind?: string | null; city?: string | null; country?: string | null; id?: string; lat?: number | null; lng?: number | null };
  notes: string;
  examples: string[];
}

/** Two or three good sentences from the host's few words. Throws with a message fit to show. */
export async function describePlace(req: DescribeRequest): Promise<string> {
  const place = { ...req.place, lat: req.place.lat ?? 0, lng: req.place.lng ?? 0 };
  const { data, error } = await supabase.functions.invoke('place-suggest', { body: { place, notes: req.notes, examples: req.examples } });
  if (error) {
    // A function's own message (e.g. the daily limit) comes back in the response body.
    let msg = '';
    try { msg = ((await (error as { context?: Response }).context?.json?.()) as { error?: string })?.error ?? ''; } catch { /* use the default */ }
    throw new Error(msg || 'We could not write it right now. Please try again.');
  }
  const text = (data as { description?: string } | null)?.description;
  if (!text) throw new Error('We could not write it right now. Please try again.');
  return text;
}

/**
 * What picking a search result changes. The name only fills when the host hasn't typed one;
 * the address is replaced by the short form; the category follows the place's kind when we know it.
 */
export function pickPatch(
  current: Pick<PlaceFieldsValue, 'name' | 'category'>,
  place: FoundPlace,
  categories: CategoryOption[],
): { patch: Partial<PlaceFieldsValue>; suggested: SuggestedField[] } {
  const patch: Partial<PlaceFieldsValue> = {
    address: shortAddress(place) || null,
    latitude: place.lat,
    longitude: place.lng,
  };
  const suggested: SuggestedField[] = [];
  if (!current.name.trim()) {
    patch.name = place.name;
    suggested.push('name');
  }
  if (place.categoryHint && categories.some((c) => c.slug === place.categoryHint) && place.categoryHint !== current.category) {
    patch.category = place.categoryHint;
    suggested.push('category');
  }
  return { patch, suggested };
}
